/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  staffList: [],
  selectedStaff: null,
  unavailabilityList: [],
  pagination: {
      page: 1,
      page_size: 10,
      total: 0,
      next: null,
  },
  filters: { 
    branch_id: null,
    service_id: null,
    active: null,
    q: null,
    aoikumo_integration_id: null,
  },
  isLoading: false,
  isError: false,
  unavailabilityPagination: {
    page: 1,
    page_size: 10,
    total: 0,
    next: null,
  },
  isLoadingUnavailability: false,
  isErrorUnavailability: false,
};

const getters = {
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
  formattedUnavailabilityPagination: (state) => {
    const { page, total, next, page_size } = state.unavailabilityPagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
};

const mutations = {
  SET_STAFF(state, staff) { state.staffList = staff; },
  SET_SELECTED_STAFF(state, staff) { state.selectedStaff = staff; },
  SET_UNAVAILABILITY(state, list) { state.unavailabilityList = list; },
  SET_PAGINATION(state, pagination) { state.pagination = pagination; },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
  SET_IS_LOADING(state, isLoading) { state.isLoading = isLoading; },
  SET_IS_ERROR(state, isError) { state.isError = isError; },
  SET_UNAVAILABILITY_PAGINATION(state, pagination) { state.unavailabilityPagination = pagination; },
  SET_IS_LOADING_UNAVAILABILITY(state, isLoading) { state.isLoadingUnavailability = isLoading; },
  SET_IS_ERROR_UNAVAILABILITY(state, isError) { state.isErrorUnavailability = isError; },
};

const actions = {
  async fetchStaff({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const { branch_id, active, q, aoikumo_integration_id } = state.filters;

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff`),
        {
          params: {
            page: state.pagination.page,
            page_size: state.pagination.page_size,
            branch_id, active, q, aoikumo_integration_id,
          },
        }
      );

      const { results, page, total, next } = res.data;
      commit('SET_STAFF', results);
      commit('SET_PAGINATION', { ...state.pagination, page, total, next });
      return res.data;
    } catch (error) {
      console.error('Fetch staff failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async getStaff({ commit, rootGetters }, staffId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}`)
      );
      commit('SET_SELECTED_STAFF', res.data);
      return res.data;
    } catch (error) {
      console.error('Get staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async createStaff({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateStaff({ rootGetters }, { staffId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Update staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteStaff({ rootGetters }, staffId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async updatePaginationAndFetch({ commit, dispatch, state }, newPagination) {
    try {
      commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
      await dispatch('fetchStaff');
      return true;
    } catch (error) {
      console.error('Update pagination and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateFiltersAndFetch({ commit, dispatch }, filters = {}) {
    try {
      commit('SET_FILTERS', filters);
      commit('SET_PAGINATION', { ...state.pagination, page: 1 });
      await dispatch('fetchStaff');
      return true;
    } catch (error) {
      console.error('Update filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async clearFiltersAndFetch({ commit, dispatch }) {
    try {
      commit('SET_FILTERS', { branch_id: null, service_id: null, active: null, q: null, aoikumo_integration_id: null });
      commit('SET_PAGINATION', { page: 1, page_size: 10, total: 0, next: null });
      await dispatch('fetchStaff');
      return true;
    } catch (error) {
      console.error('Clear filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchStaffUnavailability({ commit, rootGetters, state }, { staffId, date = null, branchId = null } = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING_UNAVAILABILITY', true);
      commit('SET_IS_ERROR_UNAVAILABILITY', false);

      const { page, page_size } = state.pagination;

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}/unavailability`),
        { params: { branch_id: branchId, date, page, page_size } }
      );

      const { results, page: currentPage, total, next } = res.data;
      commit('SET_UNAVAILABILITY', results);
      commit('SET_UNAVAILABILITY_PAGINATION', { ...state.pagination, page: currentPage, total, next });
      return res.data;
    } catch (error) {
      console.error('Fetch staff unavailability failed:', error);
      commit('SET_IS_ERROR_UNAVAILABILITY', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING_UNAVAILABILITY', false);
    }
  },

  async updateUnavailabilityPaginationAndFetch({ commit, dispatch, state }, newPagination) {
    try {
      commit('SET_UNAVAILABILITY_PAGINATION', { ...state.unavailabilityPagination, ...newPagination });
      await dispatch('fetchStaffUnavailability');
      return true;
    } catch (error) {
      console.error('Update pagination and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async createStaffUnavailability({ rootGetters }, { staffId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}/unavailability_items`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create staff unavailability failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateStaffUnavailability({ rootGetters }, { staffId, unavailabilityId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}/unavailability_items/${unavailabilityId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Update staff unavailability failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteStaffUnavailability({ rootGetters }, { staffId, unavailabilityId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}/unavailability_items/${unavailabilityId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete staff unavailability failed:', error);
      throw error.response?.data || error;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
